/**
 * Backend expects permissions in dot notation (e.g. promotions.dashboard, configuration.casino_fm).
 * UI/schema may use short keys; map them so create/update request matches Postman.
 */
const PERMISSION_KEY_TO_API = {
  // Dot notation is the single source of truth; keep map empty on purpose.
};

/**
 * Reverse map: API permission key -> UI keys used by schema or fallback form.
 * Ensures toggles show ON when the role has that permission from the API.
 */

const API_PERMISSION_TO_UI_KEYS = {
  "configuration.casino_fm": ["configuration.casino_fm"],
  "configuration.sportsbook_fm": ["configuration.sportsbook_fm"],
  "configuration.feature_management": ["configuration.feature_management"],
  "configuration.transactions": ["configuration.transactions"],
  "admin.create_account": ["admin.create_account"],
  "promotions.dashboard": ["promotions.dashboard"],
  "promotions.promo_builder": ["promotions.promo_builder"],
  "promotions.bonus_controls": ["promotions.bonus_controls"],
  "promotions.refer_a_friend": ["promotions.refer_a_friend"],
  "payments.reports.overview": ["payments.reports.overview"],
  "payments.reports.report_generator": ["payments.reports.report_generator"],
  "payments.configuration.routing": ["payments.configuration.routing"],
  "payments.configuration.delayed_payment": [
    "payments.configuration.delayed_payment",
  ],
  "payments.configuration.limits": ["payments.configuration.limits"],
  "compliance.wallet": ["compliance.wallet"],
  "compliance.withdrawal_review": ["compliance.withdrawal_review"],
  "compliance.withdrawal_review.overview": [
    "compliance.withdrawal_review.overview",
  ],
  "compliance.withdrawal_review.rules": ["compliance.withdrawal_review.rules"],
  "compliance.fraud_detection": ["compliance.fraud_detection"],
  "compliance.maintenance_mode.view": ["compliance.maintenance_mode.view"],
  "compliance.maintenance_mode.update": ["compliance.maintenance_mode.update"],
  "sportsbook.sportsbook": ["sportsbook.sportsbook"],
  "sportsbook.casino": ["sportsbook.casino"],
  "sportsbook.reporting.reporting_summary": ["sportsbook.reporting.reporting_summary"],
  "sportsbook.reporting.top_users": ["sportsbook.reporting.top_users"],
  "sportsbook.fieldbook.events_list": ["sportsbook.fieldbook.events_list"],
  "sportsbook.monitoring.latest_bets_list": ["sportsbook.monitoring.latest_bets_list"],
  "sportsbook.monitoring.sgp_risk": ["sportsbook.monitoring.sgp_risk"],
  "sportsbook.configuration.bet_limits": ["sportsbook.configuration.bet_limits"],
  "sportsbook.configuration.sgp_limits": ["sportsbook.configuration.sgp_limits"],
  "casino.reports.finance": ["casino.reports.finance"],
  "casino.reports.providers": ["casino.reports.providers"],
  "casino.reports.games": ["casino.reports.games"],
  "casino.reports.top_users": ["casino.reports.top_users"],
  "sportsbook.configuration.account_config": ["sportsbook.configuration.account_config"],
  "settings.account_config.retrieve": ["sportsbook.configuration.account_config"],
  "settings.account_config.update": ["sportsbook.configuration.account_config"],
  "casino.configuration.casino_fm": ["casino.configuration.casino_fm"],
  "casino.configuration.games": ["casino.configuration.games"],
  "casino.configuration.providers": ["casino.configuration.providers"],
  "casino.configuration.collections": ["casino.configuration.collections"],
  "casino.configuration.views": ["casino.configuration.views"],
  "casino.configuration.tags": ["casino.configuration.tags"],
  "casino.configuration.preview": ["casino.configuration.preview"],
  "casino.configuration.features": ["casino.configuration.features"],
  "casino.configuration.uploads": ["casino.configuration.uploads"],
  "poker.reporting.top_users": ["poker.reporting.top_users"],
  "poker.configuration.cash_game_limits": ["poker.configuration.cash_game_limits"],
  "poker.configuration.stakes": ["poker.configuration.stakes"],
  "poker.configuration.private_games": ["poker.configuration.private_games"],
  "poker.configuration.tournaments": ["poker.configuration.tournaments"],
  "poker.configuration.timebank": ["poker.configuration.timebank"],
  "poker.configuration.features": ["poker.configuration.features"],
  "predictions.reports": ["predictions.reports"],
  "predictions.configuration": ["predictions.configuration"],
  "configurations.profile": ["configurations.profile"],
  "configurations.state_configuration": ["configurations.state_configuration"],
  "staff_management.staff": ["staff_management.staff"],
  "staff_management.agent_audit_log": ["staff_management.agent_audit_log"],
  "staff_management.agent_audit_log.wallet": [
    "staff_management.agent_audit_log.wallet",
  ],
  "staff_management.agent_audit_log.suspensions": [
    "staff_management.agent_audit_log.suspensions",
  ],
  "staff_management.agent_audit_log.promos": [
    "staff_management.agent_audit_log.promos",
  ],
  "staff_management.agent_audit_log.sports_bets": [
    "staff_management.agent_audit_log.sports_bets",
  ],
  "staff_management.agent_audit_log.withdrawals": [
    "staff_management.agent_audit_log.withdrawals",
  ],
};

/** API permission ids are lowercase dotted paths; normalize schema/UI drift (e.g. Sportsbook.sportsbook). */
function canonicalPermissionApiKey(key) {
  if (key == null || typeof key !== "string") return key;
  return key.toLowerCase();
}

function normalizePermissionKeyForApi(key) {
  if (!key || typeof key !== "string") return key;
  const canonical = canonicalPermissionApiKey(key);
  return PERMISSION_KEY_TO_API[key] ?? PERMISSION_KEY_TO_API[canonical] ?? canonical;
}

/**
 * For each parent key whose sub-items were collapsed in the UI, push every
 * discarded child key (e.g. "staff_management.agent_audit_log.wallet") into
 * the payload so the backend still receives them with the same on/off state.
 *
 * `discardedMap` shape: { [parentKey]: [childKey1, childKey2, ...] }
 *   e.g. { "staff_management.agent_audit_log": ["wallet", "suspensions", ...] }
 */
export function expandDiscardedSubitems(activeKeys, discardedMap) {
  if (!discardedMap) return activeKeys.map((k) => canonicalPermissionApiKey(k));
  const expanded = new Set(activeKeys.map((k) => canonicalPermissionApiKey(k)));
  [...expanded].forEach((key) => {
    const subItemKeys = discardedMap[key];
    if (subItemKeys?.length) {
      subItemKeys.forEach((sub) => {
        expanded.add(canonicalPermissionApiKey(`${key}.${sub}`));
      });
    }
  });
  return [...expanded];
}

/**
 * Permission strings the API expects on create/update: leaf keys from the schema tree,
 * top-level categories with no items, plus `parent.child` keys from `discarded_subitems`.
 * Intermediate parent keys used only for UI cascade are excluded when filtering payloads.
 */
export function collectApiPermissionKeysFromSchema(schema) {
  const out = new Set();
  function walkItems(items) {
    if (!items?.length) return;
    for (const item of items) {
      if (item.items?.length) {
        walkItems(item.items);
      } else if (item.key) {
        out.add(canonicalPermissionApiKey(item.key));
      }
    }
  }
  for (const cat of schema?.available_permissions ?? []) {
    const items = cat.items ?? [];
    if (items.length) {
      walkItems(items);
    } else if (cat.category_key) {
      out.add(canonicalPermissionApiKey(cat.category_key));
    }
  }
  const discarded = schema?.discarded_subitems;
  if (discarded && typeof discarded === "object") {
    Object.entries(discarded).forEach(([parentKey, childKeys]) => {
      const parent = canonicalPermissionApiKey(parentKey);
      (childKeys ?? []).forEach((sub) => {
        out.add(canonicalPermissionApiKey(`${parent}.${sub}`));
      });
    });
  }
  return out;
}

function filterPayloadPermissionKeys(keys, schema) {
  const allowed = collectApiPermissionKeysFromSchema(schema);
  const keysOut = [...new Set(keys.map((k) => canonicalPermissionApiKey(k)))];
  if (allowed.size === 0) return keysOut;
  return keysOut.filter((k) => allowed.has(k));
}

/**
 * Every permission `key` in the schema (including nested `items`) → `false`.
 * Used for new-role defaults and sidebar preview densification.
 */
export function buildDefaultPermissionStateFromSchema(schema) {
  const perms = {};
  function walkItems(items) {
    if (!items?.length) return;
    for (const item of items) {
      if (item.items?.length) {
        if (item.key != null && item.key !== "") {
          perms[item.key] = false;
        }
        walkItems(item.items);
      } else if (item.key != null && item.key !== "") {
        perms[item.key] = false;
      }
    }
  }
  for (const cat of schema?.available_permissions ?? []) {
    const items = cat.items ?? [];
    if (items.length) {
      walkItems(items);
    } else if (cat.category_key) {
      perms[cat.category_key] = false;
    }
  }
  return perms;
}

function setPermissionTrue(acc, apiKey) {
  const k = canonicalPermissionApiKey(apiKey);
  const uiKeys = API_PERMISSION_TO_UI_KEYS[k];
  if (uiKeys && uiKeys.length > 0) {
    // Only uses UI keys, not the API key
    uiKeys.forEach((uk) => {
      acc[canonicalPermissionApiKey(uk)] = true;
    });
  } else {
    acc[k] = true;
  }
}

/**
 * Map API role to UI role shape.
 * API: { id, name, color, permissions: string[], actions: string[], has_all_permissions, is_system_role, read_only_access, in_use, etag?, ... }
 * UI: { id, value, label, name, color, permissions: Record<key, boolean>, actions: Record<key, boolean>, has_all_permissions, is_system_role, read_only_access, in_use, etag }
 *
 * `read_only_access` is a first-class BE field on the Role object. When
 * `true`, the runtime strips `actions` from `/me` for admins holding this
 * role (permissions and pages stay visible — the "see everything, do
 * nothing" persona). GET responses always report `actions: []` for
 * read-only roles, even when `has_all_permissions` is also true.
 *
 * `schema` is optional. When provided and it carries a `discarded_subitems`
 * map, the UI parent toggle is lit up if any of the recorded sub-keys is
 * true in the API payload. Lets legacy roles that only stored sub-keys
 * still show the merged toggle as ON.
 *
 */
export function apiRoleToUiRole(apiRole, schema) {
  if (!apiRole) return null;
  const permissions = (apiRole.permissions ?? []).reduce((acc, apiKey) => {
    setPermissionTrue(acc, apiKey);
    return acc;
  }, {});

  const discardedMap = schema?.discarded_subitems;
  if (discardedMap) {
    Object.entries(discardedMap).forEach(([parentKey, childKeys]) => {
      const parent = canonicalPermissionApiKey(parentKey);
      if (permissions[parent]) return; // already on
      const anyChildOn = (childKeys ?? []).some((sub) =>
        Boolean(permissions[canonicalPermissionApiKey(`${parent}.${sub}`)]),
      );
      if (anyChildOn) permissions[parent] = true;
    });
  }

  const actions = (apiRole.actions ?? []).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});
  return {
    id: apiRole.id,
    value: apiRole.id,
    label: apiRole.name ?? "",
    name: apiRole.name ?? "",
    color: apiRole.color ?? "#6b7280",
    permissions,
    actions,
    has_all_permissions: Boolean(apiRole.has_all_permissions),
    is_system_role: Boolean(apiRole.is_system_role),
    read_only_access: Boolean(apiRole.read_only_access),
    in_use: Boolean(apiRole.in_use),
    etag: apiRole.etag ?? null,
  };
}

/**
 * Map UI role to API payload for create.
 *
 * Shape: `{ name, color, permissions: string[], actions?: string[], has_all_permissions, read_only_access }`.
 *
 * When `read_only_access` is `true`, `actions` is omitted from the payload:
 * the BE silently discards any actions submitted alongside the flag, and the
 * "the right fix is not sending actions while the toggle is on" guidance
 * makes the intent explicit. `read_only_access` defaults to `false` on the
 * BE, but we always send it so create payloads are self-describing.
 */
export function uiRoleToCreatePayload(uiRole, schema) {
  const actionKeys = schema?.available_actions ? schema.available_actions.map((a) => a.key) : [];
  const permissionsRaw = (uiRole.permissions && typeof uiRole.permissions === "object")
    ? Object.keys(uiRole.permissions).filter((k) => uiRole.permissions[k])
    : [];
  const actions = (uiRole.actions && typeof uiRole.actions === "object")
    ? Object.keys(uiRole.actions).filter((k) => uiRole.actions[k])
    : [];
  const permissionsNormalized = permissionsRaw.map(normalizePermissionKeyForApi);
  const permissionsExpanded = expandDiscardedSubitems(permissionsNormalized, schema?.discarded_subitems);
  const permissions = filterPayloadPermissionKeys([...new Set(permissionsExpanded)], schema);
  const readOnlyAccess = Boolean(uiRole.read_only_access);
  const payload = {
    name: uiRole.name?.trim() ?? "",
    color: uiRole.color ?? "#6b7280",
    permissions,
    has_all_permissions: Boolean(uiRole.has_all_permissions),
    read_only_access: readOnlyAccess,
  };
  if (!readOnlyAccess) {
    payload.actions = actions.filter((a) => actionKeys.includes(a));
  }
  return payload;
}

/**
 * Map UI role to API payload for update.
 *
 * Shape: `{ name?, color?, permissions: string[], actions?: string[], has_all_permissions?, read_only_access, etag? }`.
 *
 * `read_only_access` is `bool | null` on the BE ("omit to leave unchanged"),
 * but the editor always has an authoritative UI value, so we send the
 * boolean explicitly. When it is `true`, `actions` is dropped from the
 * payload — sending it alongside a stored read-only role is rejected with
 * `ROLE_READ_ONLY`, and sending it alongside `read_only_access: true` is
 * silently discarded. Re-enabling in a single request (flip to `false` +
 * new actions in the same PUT) is allowed and covered by the non-read-only
 * branch below.
 */
export function uiRoleToUpdatePayload(uiRole, schema) {
  const actionKeys = schema?.available_actions ? schema.available_actions.map((a) => a.key) : [];
  const permissionsRaw = (uiRole.permissions && typeof uiRole.permissions === "object")
    ? Object.keys(uiRole.permissions).filter((k) => uiRole.permissions[k])
    : [];
  const permissionsNormalized = permissionsRaw.map(normalizePermissionKeyForApi);
  const permissionsExpanded = expandDiscardedSubitems(permissionsNormalized, schema?.discarded_subitems);
  const permissions = filterPayloadPermissionKeys([...new Set(permissionsExpanded)], schema);
  const actions = (uiRole.actions && typeof uiRole.actions === "object")
    ? Object.keys(uiRole.actions).filter((k) => uiRole.actions[k] && actionKeys.includes(k))
    : [];
  const readOnlyAccess = Boolean(uiRole.read_only_access);
  const payload = {
    permissions,
    has_all_permissions: Boolean(uiRole.has_all_permissions),
    read_only_access: readOnlyAccess,
  };
  if (!readOnlyAccess) {
    payload.actions = actions;
  }
  if (uiRole.name != null) payload.name = String(uiRole.name).trim();
  if (uiRole.color != null) payload.color = uiRole.color;
  if (uiRole.etag != null) payload.etag = uiRole.etag;
  return payload;
}

/**
 * Returns array of hex colors currently used by roles (for unique color validation).
 */
export function getUsedRoleColors(roles) {
  return (roles ?? []).map((r) => (r.color || "").toLowerCase()).filter(Boolean);
}

/**
 * Remove invalid permission/action keys from create payload (for retry after INVALID_PERMISSIONS / INVALID_ACTIONS).
 * Error shape: { response: { data: { detail: { invalid_permissions: string[], invalid_actions?: string[] } } } }
 * or { response: { data: { invalid_permissions: string[], invalid_actions?: string[] } } }
 *
 * `actions` is only filtered when the original payload carried it. Read-only
 * payloads (built without `actions` on purpose) must not sprout an empty
 * `actions: []` on retry — the BE rejects actions on read-only roles with
 * `ROLE_READ_ONLY` regardless of the array's contents.
 */
export function stripInvalidKeysFromPayload(payload, err) {
  const data = err?.response?.data ?? {};
  const detail = data.detail ?? data;
  const invalidPerms = detail.invalid_permissions ?? [];
  const invalidActions = detail.invalid_actions ?? [];
  if (invalidPerms.length === 0 && invalidActions.length === 0) return payload;
  const invalidSet = new Set(invalidPerms);
  const invalidActionsSet = new Set(invalidActions);
  const stripped = {
    ...payload,
    permissions: (payload.permissions ?? []).filter((p) => !invalidSet.has(p)),
  };
  if (Object.prototype.hasOwnProperty.call(payload, "actions")) {
    stripped.actions = (payload.actions ?? []).filter((a) => !invalidActionsSet.has(a));
  }
  return stripped;
}
